import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, FileText, LogOut, Search } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, shortDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FallbackBanner } from "@/components/FallbackBanner";
import { GoldProgress, StatusPill } from "@/components/StatusPill";
import { TimelinePanel } from "@/components/panels/TimelinePanel";
import { DocumentsPanel } from "@/components/panels/DocumentsPanel";
import { MessagesPanel } from "@/components/panels/MessagesPanel";
import { TasksPanel } from "@/components/panels/TasksPanel";
import { InvoicesPanel } from "@/components/panels/InvoicesPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const TABS = ["Overview", "Timeline", "Documents", "Messages", "Tasks", "Invoices"];

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [lookup, setLookup] = useState("");

  const load = useCallback(() => {
    api.get("/cases").then(({ data }) => {
      setCases(data.cases);
      setActiveId((prev) => prev || data.cases[0]?.id || null);
    });
  }, []);

  useEffect(load, [load]);

  useEffect(() => {
    if (!activeId) return;
    api.get(`/cases/${activeId}`).then(({ data }) => setDetail(data));
  }, [activeId]);

  const activeCase = detail?.case;

  const doLookup = async (e) => {
    e.preventDefault();
    if (!lookup.trim()) return;
    try {
      const { data } = await api.get("/cases/lookup", { params: { reference: lookup.trim() } });
      setActiveId(data.id);
      toast.success(`Opened ${data.reference}`);
    } catch {
      toast.error(`No case found for reference ${lookup.trim()}`);
    }
  };

  const stats = useMemo(
    () => [
      { label: "Documents", value: detail?.counts.documents ?? 0 },
      { label: "Open actions", value: detail?.counts.open_tasks ?? 0 },
      { label: "Messages", value: detail?.counts.messages ?? 0 },
      { label: "Invoices", value: detail?.counts.invoices ?? 0 },
    ],
    [detail],
  );

  return (
    <div className="app-shell lg:grid lg:grid-cols-[330px_1fr]">
      <aside className="glass-strong border-r border-white/10 p-7 lg:h-screen lg:sticky lg:top-0 flex flex-col gap-7">
        <div>
          <p className="font-display text-lg tracking-[0.3em] text-gold">VINEFORD</p>
          <p className="mt-1 text-[11px] uppercase tracking-[0.25em] text-[#6B7280]">Client Portal</p>
        </div>

        <form onSubmit={doLookup} className="relative" data-testid="case-lookup-form">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]" />
          <Input
            data-testid="case-lookup-input"
            value={lookup}
            onChange={(e) => setLookup(e.target.value)}
            placeholder="Case reference e.g. VF-2025-0141"
            className="pl-9 bg-white/5 border-white/15 text-sm"
          />
        </form>

        <div className="flex-1 overflow-y-auto scroll-thin space-y-3 -mr-2 pr-2">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#6B7280]">Your cases ({cases.length})</p>
          {cases.map((c) => (
            <button
              key={c.id}
              data-testid="sidebar-case-item"
              onClick={() => setActiveId(c.id)}
              className={`w-full text-left rounded-xl border px-4 py-4 transition-colors ${
                c.id === activeId
                  ? "border-gold/45 bg-gold/10"
                  : "border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.05]"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium tracking-wider text-gold">{c.reference}</span>
                <StatusPill value={c.status} />
              </div>
              <p className="mt-2 text-sm text-[#F3F4F6] leading-snug">{c.title}</p>
              <GoldProgress value={c.progress} className="mt-3" />
            </button>
          ))}
          {!cases.length ? <p className="text-sm text-[#9CA3AF]">No cases are linked to your account yet.</p> : null}
        </div>

        <button
          data-testid="logout-btn"
          onClick={() => {
            logout();
            navigate("/login", { replace: true });
          }}
          className="flex items-center gap-2 text-xs text-[#9CA3AF] hover:text-gold transition-colors"
        >
          <LogOut className="h-3.5 w-3.5" /> Sign out · {user?.name}
        </button>
      </aside>

      <main className="px-6 lg:px-12 py-9 space-y-8 min-w-0">
        <FallbackBanner />

        {activeCase ? (
          <>
            <header className="animate-fade-up">
              <p className="text-xs uppercase tracking-[0.28em] text-gold">{activeCase.reference}</p>
              <h1 className="mt-3 font-display text-3xl sm:text-4xl text-[#F3F4F6] leading-tight" data-testid="case-title">
                {activeCase.title}
              </h1>
              <div className="mt-5 flex flex-wrap items-center gap-4">
                <StatusPill value={activeCase.status} testId="case-status-pill" />
                <StatusPill value={activeCase.priority} label={`${activeCase.priority} priority`} />
                <span className="text-sm text-[#9CA3AF]">Opened {shortDate(activeCase.created_at)}</span>
                <Button
                  data-testid="case-summary-pdf-btn"
                  size="sm"
                  variant="outline"
                  className="ml-auto rounded-full border-white/20 bg-transparent hover:bg-white/10"
                  onClick={() =>
                    downloadFile(`/cases/${activeCase.id}/summary`, `${activeCase.reference}-summary.pdf`, { format: "pdf" })
                      .catch(() => toast.error("Export failed"))
                  }
                >
                  <FileText className="h-4 w-4 mr-2 text-gold" /> Summary PDF
                </Button>
                <Button
                  data-testid="case-timeline-csv-btn"
                  size="sm"
                  variant="ghost"
                  className="rounded-full text-[#9CA3AF] hover:bg-white/10 hover:text-gold"
                  onClick={() =>
                    downloadFile(`/cases/${activeCase.id}/summary`, `${activeCase.reference}-timeline.csv`, { format: "csv" })
                      .catch(() => toast.error("Export failed"))
                  }
                >
                  <Download className="h-4 w-4 mr-2" /> Timeline CSV
                </Button>
              </div>
            </header>

            <Tabs defaultValue="Overview" className="min-w-0">
              <TabsList className="bg-white/[0.04] border border-white/10 rounded-full p-1 flex-wrap h-auto">
                {TABS.map((t) => (
                  <TabsTrigger
                    key={t}
                    value={t}
                    data-testid={`tab-${t.toLowerCase()}`}
                    className="rounded-full px-4 py-1.5 text-sm data-[state=active]:bg-gold data-[state=active]:text-navy text-[#9CA3AF]"
                  >
                    {t}
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="Overview" className="mt-8 animate-fade-up">
                <div className="grid gap-6 lg:grid-cols-3">
                  <div className="glass rounded-2xl p-7 lg:col-span-2 space-y-7">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-[#9CA3AF]">Case progress</p>
                      <div className="mt-4 flex items-end gap-4">
                        <span className="font-display text-4xl text-gold" data-testid="case-progress-value">
                          {activeCase.progress}%
                        </span>
                        <span className="pb-2 text-sm text-[#9CA3AF]">{activeCase.status.replace(/_/g, " ")}</span>
                      </div>
                      <GoldProgress value={activeCase.progress} testId="case-progress-bar" className="mt-4 h-2" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-[#9CA3AF]">Instruction</p>
                      <p className="mt-3 text-sm leading-relaxed text-[#D1D5DB]">{activeCase.description}</p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {stats.map((s) => (
                        <div key={s.label} className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-4">
                          <p className="font-display text-2xl text-[#F3F4F6]">{s.value}</p>
                          <p className="mt-1 text-[11px] uppercase tracking-widest text-[#6B7280]">{s.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="glass rounded-2xl p-7 space-y-5 h-fit">
                    <p className="text-xs uppercase tracking-[0.2em] text-[#9CA3AF]">Your case team</p>
                    {[
                      ["Case manager", activeCase.manager_name],
                      ["Lead investigator", activeCase.investigator_name || "To be assigned"],
                      ["Category", activeCase.category],
                      ["Case file ID", activeCase.arkcase_id || "—"],
                      ["Last update", shortDate(activeCase.updated_at)],
                    ].map(([k, v]) => (
                      <div key={k} className="flex items-start justify-between gap-4 border-b border-white/5 pb-3 last:border-0">
                        <span className="text-xs uppercase tracking-wider text-[#6B7280]">{k}</span>
                        <span className="text-sm text-[#F3F4F6] text-right">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="Timeline" className="mt-8 animate-fade-up">
                <TimelinePanel caseId={activeCase.id} />
              </TabsContent>
              <TabsContent value="Documents" className="mt-8 animate-fade-up">
                <DocumentsPanel caseId={activeCase.id} uploadLabel="Evidence upload" />
              </TabsContent>
              <TabsContent value="Messages" className="mt-8 animate-fade-up">
                <MessagesPanel caseId={activeCase.id} currentRole={user?.role} />
              </TabsContent>
              <TabsContent value="Tasks" className="mt-8 animate-fade-up">
                <TasksPanel caseId={activeCase.id} />
              </TabsContent>
              <TabsContent value="Invoices" className="mt-8 animate-fade-up">
                <InvoicesPanel caseId={activeCase.id} />
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <div className="glass rounded-2xl p-12 text-center">
            <h2 className="font-display text-2xl text-[#F3F4F6]">No case selected</h2>
            <p className="mt-3 text-sm text-[#9CA3AF]">Search for a case reference to get started.</p>
          </div>
        )}
      </main>
    </div>
  );
}
